/**
 * Pushes the lab's cleaning and feature knobs onto the chart's pens, one way:
 * range/gate become pen filters, denoise and the rolling feature become rolling
 * overlays.
 *
 * Windows are pushed in wall time rather than samples. The lab counts them in
 * grid rows, and a pen may be drawn raw, so `window * interval` is what the row
 * count spans on the chart.
 */
import { useLingui } from '@lingui/react/macro'
import {
  CleaningOp,
  ExperimentConfig,
  IntervalUnit,
} from 'features/timeSeries/experiment/types'
import { Dispatch, SetStateAction, useMemo } from 'react'
import { SmoothingConfig, ThresholdFilterConfig } from 'types'

const UNIT_SECONDS: Record<IntervalUnit, number> = {
  seconds: 1,
  minutes: 60,
  hours: 3600,
}

const DENOISE_METHODS: Record<string, SmoothingConfig['method']> = {
  denoise_median: 'median',
  denoise_mean: 'avg',
}

const gridSeconds = (config: ExperimentConfig): number =>
  config.interval * UNIT_SECONDS[config.interval_unit]

const labTags = (config: ExperimentConfig): string[] =>
  [config.target, ...config.drivers].filter((tag) => tag !== '')

const opsFor = (config: ExperimentConfig, tag: string): CleaningOp[] =>
  config.cleaning_plan.tags?.[tag]?.ops ?? []

/**
 * Range and gate as a `between` filter on the tag's own pen, which is inclusive
 * on both ends like the ops are. A tag carrying both gets the intersection,
 * since both judge its own values; the gate's row-wide drop is not reproduced.
 */
export const cleaningFilterPush = (
  config: ExperimentConfig
): Record<string, ThresholdFilterConfig> => {
  const filters: Record<string, ThresholdFilterConfig> = {}

  labTags(config).forEach((tag) => {
    const bounded = opsFor(config, tag).filter(
      (op) => op.op === 'range' || op.op === 'gate'
    )
    if (bounded.length === 0) return

    const min = Math.max(...bounded.map((op) => op.min ?? -Infinity))
    const max = Math.min(...bounded.map((op) => op.max ?? Infinity))
    if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return

    filters[tag] = {
      type: 'threshold_filter',
      sensorId: tag,
      enabled: true,
      condition: 'between',
      value1: min,
      value2: max,
    }
  })

  return filters
}

/** Denoise as a centered overlay that tolerates partial windows, like the op. */
export const denoisePush = (
  config: ExperimentConfig
): Record<string, SmoothingConfig> => {
  const smoothing: Record<string, SmoothingConfig> = {}

  labTags(config).forEach((tag) => {
    const op = opsFor(config, tag).find(
      (candidate) => candidate.op in DENOISE_METHODS
    )
    if (!op) return

    const window = Math.max(1, Math.round(op.window ?? 3))
    smoothing[tag] = {
      type: 'smoothing',
      sensorId: tag,
      enabled: true,
      method: DENOISE_METHODS[op.op],
      basis: 'duration',
      windowSeconds: window * gridSeconds(config),
      windowSize: window,
      alignment: 'centered',
      display: 'overlay',
      requireFullWindow: false,
    }
  })

  return smoothing
}

/**
 * The `rolling_mean` features: trailing and full-window, shifted a period for
 * the target so its own current value stays out of its window, which is the
 * `offset=1` the target features are built with.
 */
export const rollingFeaturePush = (
  config: ExperimentConfig
): Record<string, SmoothingConfig> => {
  const window = Math.max(1, Math.round(config.feature.rolling_window))
  const windowSeconds = window * gridSeconds(config)
  const smoothing: Record<string, SmoothingConfig> = {}

  const add = (tag: string, alignment: SmoothingConfig['alignment']) => {
    if (tag === '') return
    smoothing[tag] = {
      type: 'smoothing',
      sensorId: tag,
      enabled: true,
      method: 'avg',
      basis: 'duration',
      windowSeconds,
      windowSize: window,
      alignment,
      display: 'overlay',
      requireFullWindow: true,
    }
  }

  if (config.feature.include_self_history) add(config.target, 'trailingShifted')
  if (config.feature.include_driver_history) {
    config.drivers.forEach((driver) => add(driver, 'trailing'))
  }

  return smoothing
}

export interface LabPush {
  active: boolean
  setActive: (active: boolean) => void
  /** Set when the lab config has nothing of this kind to draw. */
  unavailableReason: string | null
}

const matches = <T extends object>(
  expected: T,
  current: T | undefined
): boolean =>
  current !== undefined &&
  (Object.keys(expected) as Array<keyof T>).every(
    (key) => expected[key] === current[key]
  )

const buildPush = <T extends object>(
  expected: Record<string, T>,
  current: Record<string, T & { enabled: boolean }>,
  setCurrent: Dispatch<
    SetStateAction<Record<string, T & { enabled: boolean }>>
  >,
  emptyReason: string
): LabPush => {
  const tags = Object.keys(expected)

  return {
    active:
      tags.length > 0 &&
      tags.every((tag) => matches(expected[tag], current[tag])),
    unavailableReason: tags.length === 0 ? emptyReason : null,
    setActive: (active) => {
      setCurrent((previous) => {
        const next = { ...previous }
        tags.forEach((tag) => {
          if (active) {
            next[tag] = expected[tag] as T & { enabled: boolean }
            return
          }
          // Only hand back what this push owns, so turning it off cannot
          // disable a pen another push or the user has since claimed.
          const existing = previous[tag]
          if (existing && matches(expected[tag], existing)) {
            next[tag] = { ...existing, enabled: false }
          }
        })
        return next
      })
    },
  }
}

export interface LabTransformsOnChart {
  cleaningFilters: LabPush
  denoise: LabPush
  rollingFeature: LabPush
}

export const useLabTransformsOnChart = (
  config: ExperimentConfig,
  thresholdFilters: Record<string, ThresholdFilterConfig>,
  setThresholdFilters: Dispatch<
    SetStateAction<Record<string, ThresholdFilterConfig>>
  >,
  smoothingConfigs: Record<string, SmoothingConfig>,
  setSmoothingConfigs: Dispatch<SetStateAction<Record<string, SmoothingConfig>>>
): LabTransformsOnChart => {
  const { t } = useLingui()

  const filters = useMemo(() => cleaningFilterPush(config), [config])
  const denoise = useMemo(() => denoisePush(config), [config])
  const rolling = useMemo(() => rollingFeaturePush(config), [config])

  return {
    cleaningFilters: buildPush(
      filters,
      thresholdFilters,
      setThresholdFilters,
      t`No range or gate ops in this plan.`
    ),
    denoise: buildPush(
      denoise,
      smoothingConfigs,
      setSmoothingConfigs,
      t`No denoise ops in this plan.`
    ),
    rollingFeature: buildPush(
      rolling,
      smoothingConfigs,
      setSmoothingConfigs,
      t`This config builds no rolling features.`
    ),
  }
}
